import { createPillButton, pillButtonHtml, type PillButtonOptions } from './pillButton.js';
import { attachPillGroupOverflow } from './pillButtonGroupOverflow.js';

export type PillButtonGroupItem = {
  label: string;
} & PillButtonOptions;

export type PillButtonGroupOptions = {
  large?: boolean;
  small?: boolean;
  class?: string;
  ariaLabel?: string;
  /** Une seule ligne : le surplus passe dans un menu « ⋯ ». */
  overflow?: boolean;
};

function groupClassName(opts: PillButtonGroupOptions = {}): string {
  const parts = ['btn-pill-group'];
  if (opts.large) parts.push('btn-pill-group--lg');
  if (opts.small) parts.push('btn-pill-group--sm');
  if (opts.class) parts.push(opts.class);
  return parts.join(' ');
}

/** HTML d’un groupe de pills collées (templates innerHTML). */
export function pillButtonGroupHtml(
  items: Array<PillButtonGroupItem & { href: string }>,
  opts: PillButtonGroupOptions = {}
): string {
  const buttons = items
    .map((item) => {
      const { label, large: _large, small: _small, ...rest } = item;
      return pillButtonHtml(label, {
        ...rest,
        large: false,
        small: false,
      });
    })
    .join('');
  const aria = opts.ariaLabel ? ` aria-label="${opts.ariaLabel}"` : '';
  return `<div class="${groupClassName(opts)}" role="group"${aria}>${buttons}</div>`;
}

/** Groupe de pills en DOM (liens et/ou boutons). */
export function createPillButtonGroup(
  items: PillButtonGroupItem[],
  opts: PillButtonGroupOptions = {}
): HTMLElement {
  const group = document.createElement('div');
  group.className = groupClassName(opts);
  group.setAttribute('role', 'group');
  if (opts.ariaLabel) group.setAttribute('aria-label', opts.ariaLabel);

  for (const item of items) {
    const { label, large: _large, small: _small, ...rest } = item;
    group.appendChild(
      createPillButton(label, {
        ...rest,
        large: false,
        small: false,
      })
    );
  }
  if (opts.overflow) attachPillGroupOverflow(group);
  return group;
}

/** Regroupe des pills déjà créées (handlers / classes métier conservés). */
export function wrapInPillButtonGroup(
  buttons: HTMLElement[],
  opts: PillButtonGroupOptions = {}
): HTMLElement {
  const group = document.createElement('div');
  group.className = groupClassName(opts);
  group.setAttribute('role', 'group');
  if (opts.ariaLabel) group.setAttribute('aria-label', opts.ariaLabel);

  for (const btn of buttons) {
    btn.classList.remove('btn-pill--lg', 'btn-pill--sm');
    group.appendChild(btn);
  }
  if (opts.overflow) attachPillGroupOverflow(group);
  return group;
}
